import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import CustomSelect from '../components/CustomSelect';

interface TrainingCourse {
  id: number;
  name: string;
  description: string | null;
  category: string;
  duration: number | null;
  isMandatory: boolean;
  validityPeriod: number | null;
  isActive: boolean;
  createdAt: string;
}

interface TrainingRecord {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  courseId: number;
  courseName: string;
  courseDescription?: string | null;
  courseCategory: string;
  courseDuration?: number | null;
  isMandatory: boolean;
  status: string;
  completedDate: string | null;
  expiryDate: string | null;
  score: number | null;
  notes: string | null;
  assignedDate: string;
  createdAt: string;
}

interface StaffMember {
  id: number;
  name: string;
  email: string;
  role: string;
  staffType: string;
}

const Training: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [activeTab, setActiveTab] = useState<'courses' | 'records' | 'my-training'>('my-training');
  const [courses, setCourses] = useState<TrainingCourse[]>([]);
  const [records, setRecords] = useState<TrainingRecord[]>([]);
  const [myTraining, setMyTraining] = useState<TrainingRecord[]>([]);
  const [trainingSummary, setTrainingSummary] = useState({ total: 0, completed: 0, pending: 0, expired: 0 });
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<TrainingCourse | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<TrainingRecord | null>(null);

  const [courseForm, setCourseForm] = useState({
    name: '',
    description: '',
    category: 'general',
    duration: '',
    isMandatory: false,
    validityPeriod: ''
  });

  const [assignForm, setAssignForm] = useState({
    userId: '',
    courseId: '',
    notes: ''
  });

  const [updateForm, setUpdateForm] = useState({
    status: '',
    score: '',
    notes: ''
  });

  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'health_safety', label: 'Health & Safety' },
    { value: 'clinical', label: 'Clinical Skills' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'safeguarding', label: 'Safeguarding' },
    { value: 'medication', label: 'Medication' },
    { value: 'first_aid', label: 'First Aid' },
    { value: 'manual_handling', label: 'Manual Handling' },
    { value: 'infection_control', label: 'Infection Control' },
    { value: 'communication', label: 'Communication' }
  ];

  useEffect(() => {
    if (!isAdmin && (activeTab === 'courses' || activeTab === 'records')) {
      setActiveTab('my-training');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'courses' && isAdmin) {
        const coursesRes = await api.get('/training/courses');
        setCourses(coursesRes.data.data);
      }

      if (activeTab === 'records' && isAdmin) {
        const recordsRes = await api.get('/training/records');
        setRecords(recordsRes.data.data);

        const staffRes = await api.get('/users/staff');
        setStaffMembers(staffRes.data.data.staff);
      }

      if (activeTab === 'my-training') {
        const myTrainingRes = await api.get('/training/my-training');
        setMyTraining(myTrainingRes.data.data.records);
        setTrainingSummary(myTrainingRes.data.data.summary);
        
        if (isAdmin) {
          const coursesRes = await api.get('/training/courses');
          setCourses(coursesRes.data.data);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch training data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/training/courses', {
        name: courseForm.name,
        description: courseForm.description || null,
        category: courseForm.category,
        duration: courseForm.duration ? parseInt(courseForm.duration) : null,
        isMandatory: courseForm.isMandatory,
        validityPeriod: courseForm.validityPeriod ? parseInt(courseForm.validityPeriod) : null
      });
      setShowCourseModal(false);
      setCourseForm({ name: '', description: '', category: 'general', duration: '', isMandatory: false, validityPeriod: '' });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create course');
    }
  };

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourse) return;
    try {
      await api.put(`/training/courses/${selectedCourse.id}`, {
        name: courseForm.name,
        description: courseForm.description || null,
        category: courseForm.category,
        duration: courseForm.duration ? parseInt(courseForm.duration) : null,
        isMandatory: courseForm.isMandatory,
        validityPeriod: courseForm.validityPeriod ? parseInt(courseForm.validityPeriod) : null
      });
      setShowCourseModal(false);
      setSelectedCourse(null);
      setCourseForm({ name: '', description: '', category: 'general', duration: '', isMandatory: false, validityPeriod: '' });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update course');
    }
  };

  const handleAssignTraining = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/training/assign', {
        userId: parseInt(assignForm.userId),
        courseId: parseInt(assignForm.courseId),
        notes: assignForm.notes || null
      });
      setShowAssignModal(false);
      setAssignForm({ userId: '', courseId: '', notes: '' });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign training');
    }
  };

  const handleUpdateRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRecord) return;
    try {
      await api.put(`/training/records/${selectedRecord.id}`, {
        status: updateForm.status,
        score: updateForm.score ? parseInt(updateForm.score) : null,
        notes: updateForm.notes || null
      });
      setShowUpdateModal(false);
      setSelectedRecord(null);
      setUpdateForm({ status: '', score: '', notes: '' });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update training record');
    }
  };

  const openEditCourse = (course: TrainingCourse) => {
    setSelectedCourse(course);
    setCourseForm({
      name: course.name,
      description: course.description || '',
      category: course.category,
      duration: course.duration?.toString() || '',
      isMandatory: course.isMandatory,
      validityPeriod: course.validityPeriod?.toString() || ''
    });
    setShowCourseModal(true);
  };

  const openUpdateRecord = (record: TrainingRecord) => {
    setSelectedRecord(record);
    setUpdateForm({
      status: record.status,
      score: record.score?.toString() || '',
      notes: record.notes || ''
    });
    setShowUpdateModal(true);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      not_started: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800'
    };
    const labels: Record<string, string> = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      completed: 'Completed',
      expired: 'Expired'
    };
    return (
      <span className={`px-2 py-1 badge-pill ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getCategoryLabel = (category: string) => {
    return categories.find(c => c.value === category)?.label || category;
  };

  const isExpiringSoon = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expiry <= thirtyDaysFromNow && expiry >= new Date();
  };

  const isExpired = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const filteredCourses = courses.filter(course => {
    if (categoryFilter !== 'all' && course.category !== categoryFilter) return false;
    return true;
  });

  const filteredRecords = records.filter(record => {
    if (statusFilter !== 'all' && record.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && record.courseCategory !== categoryFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Training</h1>
          <p className="text-gray-600">Manage training courses and track completion</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={() => { setSelectedCourse(null); setCourseForm({ name: '', description: '', category: 'general', duration: '', isMandatory: false, validityPeriod: '' }); setShowCourseModal(true); }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Add Course
            </button>
            <button
              onClick={() => setShowAssignModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Assign Training
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('my-training')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'my-training' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            My Training
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setActiveTab('courses')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'courses' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Courses
              </button>
              <button
                onClick={() => setActiveTab('records')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'records' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                All Records
              </button>
            </>
          )}
        </nav>
      </div>

      {activeTab === 'my-training' && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-primary-600">
              <p className="text-gray-600 text-sm">Total Assigned</p>
              <p className="text-2xl font-bold text-gray-900">{trainingSummary.total}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-600">
              <p className="text-gray-600 text-sm">Completed</p>
              <p className="text-2xl font-bold text-green-600">{trainingSummary.completed}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-600">
              <p className="text-gray-600 text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{trainingSummary.pending}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-600">
              <p className="text-gray-600 text-sm">Expired</p>
              <p className="text-2xl font-bold text-red-600">{trainingSummary.expired}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {myTraining.map((record) => (
                  <tr key={record.id} className={isExpired(record.expiryDate) ? 'bg-red-50' : isExpiringSoon(record.expiryDate) ? 'bg-yellow-50' : ''}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900">{record.courseName}</span>
                        {record.isMandatory && (
                          <span className="ml-2 px-2 py-0.5 badge-pill bg-red-100 text-red-800">Required</span>
                        )}
                      </div>
                      {record.courseDescription && (
                        <p className="text-sm text-gray-500 mt-1">{record.courseDescription}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {getCategoryLabel(record.courseCategory)}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(isExpired(record.expiryDate) ? 'expired' : record.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {record.completedDate ? new Date(record.completedDate).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {record.expiryDate ? (
                        <span className={isExpired(record.expiryDate) ? 'text-red-600 font-medium' : isExpiringSoon(record.expiryDate) ? 'text-yellow-600 font-medium' : 'text-gray-500'}>
                          {new Date(record.expiryDate).toLocaleDateString()}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      {record.status !== 'completed' && (
                        <button
                          onClick={() => openUpdateRecord(record)}
                          className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                        >
                          Update
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {myTraining.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      No training assigned yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'courses' && isAdmin && (
        <div>
          <div className="mb-4 flex gap-4 flex-wrap">
            <div className="w-full sm:w-60">
              <CustomSelect
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={[
                  { value: 'all', label: 'All Categories' },
                  ...categories.map(cat => ({ value: cat.value, label: cat.label })),
                ]}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCourses.map((course) => (
              <div key={course.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-900">{course.name}</h3>
                  {course.isMandatory && (
                    <span className="px-2 py-0.5 badge-pill bg-red-100 text-red-800">Required</span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-3">{course.description || 'No description'}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 badge-pill bg-primary-100 text-primary-800">
                    {getCategoryLabel(course.category)}
                  </span>
                  {course.duration && (
                    <span className="px-2 py-1 badge-pill bg-gray-100 text-gray-800">
                      {course.duration} mins
                    </span>
                  )}
                  {course.validityPeriod && (
                    <span className="px-2 py-1 badge-pill bg-blue-100 text-blue-800">
                      Valid {course.validityPeriod} months
                    </span>
                  )}
                </div>
                <button
                  onClick={() => openEditCourse(course)}
                  className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                >
                  Edit
                </button>
              </div>
            ))}
            {filteredCourses.length === 0 && (
              <div className="col-span-3 text-center py-8 text-gray-500">
                No courses found
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'records' && isAdmin && (
        <div>
          <div className="mb-4 flex gap-4 flex-wrap">
            <div className="w-full sm:w-60">
              <CustomSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All Statuses' },
                  { value: 'not_started', label: 'Not Started' },
                  { value: 'in_progress', label: 'In Progress' },
                  { value: 'completed', label: 'Completed' },
                ]}
              />
            </div>
            <div className="w-full sm:w-60">
              <CustomSelect
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={[
                  { value: 'all', label: 'All Categories' },
                  ...categories.map(cat => ({ value: cat.value, label: cat.label })),
                ]}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRecords.map((record) => (
                  <tr key={record.id} className={isExpired(record.expiryDate) ? 'bg-red-50' : isExpiringSoon(record.expiryDate) ? 'bg-yellow-50' : ''}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{record.userName}</div>
                      <div className="text-sm text-gray-500">{record.userEmail}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900">{record.courseName}</span>
                        {record.isMandatory && (
                          <span className="ml-2 px-2 py-0.5 badge-pill bg-red-100 text-red-800">Required</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{getCategoryLabel(record.courseCategory)}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(isExpired(record.expiryDate) ? 'expired' : record.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(record.assignedDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {record.completedDate ? new Date(record.completedDate).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {record.expiryDate ? (
                        <span className={isExpired(record.expiryDate) ? 'text-red-600 font-medium' : isExpiringSoon(record.expiryDate) ? 'text-yellow-600 font-medium' : 'text-gray-500'}>
                          {new Date(record.expiryDate).toLocaleDateString()}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openUpdateRecord(record)}
                        className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                      >
                        Update
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      No training records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCourseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {selectedCourse ? 'Edit Course' : 'Add Training Course'}
            </h2>
            <form onSubmit={selectedCourse ? handleUpdateCourse : handleCreateCourse}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course Name *</label>
                  <input
                    type="text"
                    value={courseForm.name}
                    onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })}
                    className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <CustomSelect
                    value={courseForm.category}
                    onChange={(v) => setCourseForm({ ...courseForm, category: v })}
                    options={categories.map(cat => ({ value: cat.value, label: cat.label }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    value={courseForm.duration}
                    onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })}
                    className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Validity Period (months)</label>
                  <input
                    type="number"
                    value={courseForm.validityPeriod}
                    onChange={(e) => setCourseForm({ ...courseForm, validityPeriod: e.target.value })}
                    className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    min="0"
                    placeholder="Leave blank for no expiry"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isMandatory"
                    checked={courseForm.isMandatory}
                    onChange={(e) => setCourseForm({ ...courseForm, isMandatory: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isMandatory" className="ml-2 text-sm text-gray-700">
                    Mandatory training
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowCourseModal(false); setSelectedCourse(null); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {selectedCourse ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Assign Training</h2>
            <form onSubmit={handleAssignTraining}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member *</label>
                  <CustomSelect
                    value={assignForm.userId}
                    onChange={(v) => setAssignForm({ ...assignForm, userId: v })}
                    placeholder="Select staff member"
                    options={[
                      { value: '', label: 'Select staff member' },
                      ...staffMembers.map(staff => ({ value: String(staff.id), label: `${staff.name} (${staff.email})` })),
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
                  <CustomSelect
                    value={assignForm.courseId}
                    onChange={(v) => setAssignForm({ ...assignForm, courseId: v })}
                    placeholder="Select course"
                    options={[
                      { value: '', label: 'Select course' },
                      ...courses.map(course => ({
                        value: String(course.id),
                        label: `${course.name}${course.isMandatory ? ' (Required)' : ''}`,
                      })),
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={assignForm.notes}
                    onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    placeholder="Optional notes for the staff member"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Assign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showUpdateModal && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Update Training Record</h2>
            <p className="text-gray-600 mb-4">
              <strong>{selectedRecord.courseName}</strong>
              {selectedRecord.userName && ` - ${selectedRecord.userName}`}
            </p>
            <form onSubmit={handleUpdateRecord}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <CustomSelect
                    value={updateForm.status}
                    onChange={(v) => setUpdateForm({ ...updateForm, status: v })}
                    options={[
                      { value: 'not_started', label: 'Not Started' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Score (%)</label>
                  <input
                    type="number"
                    value={updateForm.score}
                    onChange={(e) => setUpdateForm({ ...updateForm, score: e.target.value })}
                    className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    min="0"
                    max="100"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={updateForm.notes}
                    onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowUpdateModal(false); setSelectedRecord(null); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Training;
